import React, { createContext, useContext, useState, ReactNode } from 'react';
import { X } from 'lucide-react';
import styles from './Notification.module.css'; // Adjust the path as needed

// Define allowed notification types.
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

// Define the Notification object type.
export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
}

// Define notification params interface
export interface NotificationParams {
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
}

// Define the shape of the Notification context.
export interface NotificationContextType {
  addNotification: (type: NotificationType, message: string, duration?: number) => string;
  removeNotification: (id: string) => void;
  showNotification: (params: NotificationParams) => string;
}

// Create the context.
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (type: NotificationType, message: string, duration = 5000): string => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNotifications(prev => [...prev, { id, type, message }]);
    
    if (duration) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  };

  const showNotification = ({ type, message, title, duration = 5000 }: NotificationParams): string => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNotifications(prev => [...prev, { id, type, message, title }]);
    
    if (duration) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  };

  const removeNotification = (id: string): void => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ addNotification, removeNotification, showNotification }}>
      {children}
      <div className={styles.container}>
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`${styles.notification} ${
              notification.type === 'success'
                ? styles.success
                : notification.type === 'error'
                ? styles.error
                : notification.type === 'warning'
                ? styles.warning
                : styles.info
            }`}
          >
            {notification.title && <h4 className={styles.title}>{notification.title}</h4>}
            <span>{notification.message}</span>
            <button 
              onClick={() => removeNotification(notification.id)} 
              className={styles.closeButton}
              aria-label="Close notification"
            >
              <X size={18} />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

// Custom hook to use notifications
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
